import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';
import { parseDateFromDatePicker } from 'services/DateService';

import { TextbookFileLink } from 'molecules';
import type { IHomeworkAssignment, ITextbookFile } from 'core';

interface IHomeworkAssignmentCard {
  assignment: IHomeworkAssignment;
  /** ТЗ 0026: файлы учебника занятия. */
  textbookFiles?: ITextbookFile[];
  /**
   * Дата, с которой откроются материалы занятия. Пусто — материалы доступны
   * уже сейчас. Владелец решил блокировку по дате не снимать, поэтому
   * карточка обязана честно назвать дату, а не обещать недоступное.
   */
  textbookAvailableFrom?: string | null;
  /**
   * Группа «без расписания»: срок сдачи не показываем, а заблокированный
   * учебник описываем без даты. Блокировка при этом остаётся прежней.
   */
  hideDates?: boolean;
  className?: string;
}

const URL_RE = /(https?:\/\/[^\s]+)/g;

/**
 * The assignment text is rendered as PLAIN text (never as HTML) — React
 * escapes it, so teacher-authored content cannot inject markup. Bare URLs are
 * linkified by splitting the string, which keeps every fragment escaped.
 */
const renderTextWithLinks = (text: string) =>
  text.split(URL_RE).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      // eslint-disable-next-line react/no-array-index-key
      <a key={index} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      part
    ),
  );

/**
 * Read-only assignment block: title, informational due date, safe text и —
 * с ТЗ 0026 — блок учебника занятия.
 *
 * Прежний вариант при отсутствии задания печатал строку «Задание смотри в
 * материалах урока»: без ссылки, без имени файла, и при этом вкладка
 * «Материалы» могла быть скрыта или заблокирована по дате. Теперь карточка
 * называет конкретный файл и либо открывает его, либо честно говорит, с
 * какого числа он появится.
 */
export const HomeworkAssignmentCard = memo(
  ({
    assignment,
    textbookFiles = [],
    textbookAvailableFrom = null,
    hideDates = false,
    className,
  }: IHomeworkAssignmentCard) => {
    const { t } = useTranslation();
    const hasTextbook = textbookFiles.length > 0;

    return (
      <Wrapper className={className}>
        <Title>{assignment.title || t('homework.assignment.title')}</Title>
        {assignment.dueDate && !hideDates && (
          <DueDate>
            {t('homework.assignment.due', {
              date: parseDateFromDatePicker(DateTime.fromISO(assignment.dueDate).toJSDate()),
            })}
          </DueDate>
        )}
        {assignment.hasTeacherAssignment && <Text>{renderTextWithLinks(assignment.text)}</Text>}

        <TextbookBlock>
          <TextbookTitle>
            {assignment.hasTeacherAssignment
              ? t('homework.textbook.title')
              : t('homework.textbook.isTheTask')}
          </TextbookTitle>
          {assignment.textbookRef && (
            <TextbookRef>
              {t('homework.textbook.section', { ref: assignment.textbookRef })}
            </TextbookRef>
          )}
          {/* eslint-disable-next-line no-nested-ternary */}
          {!hasTextbook ? (
            <Muted>{t('homework.textbook.missing')}</Muted>
          ) : textbookAvailableFrom ? (
            <Muted>
              {hideDates
                ? t('homework.textbook.availableLater')
                : t('homework.textbook.availableFrom', { date: textbookAvailableFrom })}
            </Muted>
          ) : (
            textbookFiles.map((file) => <TextbookFileLink key={file.id} file={file} />)
          )}
        </TextbookBlock>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  background-color: ${Colors.bg_gray};
  border-radius: 8px;
  padding: 20px;

  ${tablet} {
    padding: 24px;
  }
`;

const Title = styled.h3`
  margin: 0 0 8px;
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
`;

const DueDate = styled.p`
  margin: 0 0 12px;
  color: ${Colors.gray};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const Text = styled.p`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const TextbookBlock = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${Colors.additional_gray};
`;

const TextbookTitle = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const TextbookRef = styled.p`
  margin: 4px 0 0;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const Muted = styled.p`
  margin: 8px 0 0;
  color: ${Colors.gray};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;
